import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { Grid, Typography, Select, Box } from "@mui/material";

import CachedIcon from "@mui/icons-material/Cached";

import { ReactComponent as GeneralIcon } from "../../../vendor/waf/general.svg";
import { ReactComponent as RuleIcon } from "../../../vendor/waf/rule.svg";
import { ReactComponent as ConfirmIcon } from "../../../vendor/button/confirm.svg";

import useZone from "../../../hooks/user/useZone";
import useAuth from "../../../hooks/useAuth";
import useLLMConfig from "../../../hooks/user/useLLMConfig";
import { ConfigAction, UserType } from "../../../utils/constants";
import { Button, Divider, MenuItem, SnackbarAlert } from "../../../components/pages/common/styled";

function LLMConfigHeader({ title, url }) {
  const navigate = useNavigate();
  const { configZone } = useParams();
  const zoneUid = configZone;
  const { isAuthenticated, userType } = useAuth();
  const { zoneList, settingApply, getZonesForItems } = useZone();
  const { getLLMConfig, getLLMPromptScanners, getLLMOutputScanners, setErr } =
    useLLMConfig();

  const [snackOpen, setSnackOpen] = React.useState(false);
  const handleSnackClose = () => {
    setSnackOpen(false);
  };
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getZonesForItems();
    }
    return () => setErr(null);
  }, [isAuthenticated, getZonesForItems, setErr]);

  const selectCurrentZone = (event) => {
    setErr(null);
    navigate(`/llm/${event.target.value}/guard/${url}`);
  };
  const refresh = () => {
    switch (url) {
      case "config":
        getLLMConfig(zoneUid, true);
        break;
      case "config/prompt_scanners":
        getLLMPromptScanners(zoneUid, true);
        break;
      case "config/output_scanners":
        getLLMOutputScanners(zoneUid, true);
        break;
      default:
        break;
    }
  };
  const apply = async () => {
    setLoading(true);
    const zone = zoneList?.find((s) => s.id === zoneUid);
    if (zone) {
      const result = await settingApply(zone.zone_id, ConfigAction.LLM | ConfigAction.RATE_LIMIT);
      setMessage(result.msg);
      setSuccess(result.status);
    }
    setLoading(false);
    setSnackOpen(true);
  };
  const gotoLLMConfig = (linkURL) => {
    navigate(`/llm/${zoneUid}/guard/${linkURL}`);
  };

  const tabInfo = [
    { url: "config", icon: <GeneralIcon />, title: "General" },
    { url: "config/prompt_scanners", icon: <RuleIcon />, title: "Prompt Scanners" },
    { url: "config/output_scanners", icon: <RuleIcon />, title: "Output Scanners" },
    { url: "config/api_tokens", icon: <RuleIcon />, title: "API Tokens" },
  ];

  return (
    <React.Fragment>
      <Helmet title={"LLM Configuration"} />
      <Grid container sx={{ display: "flex", alignItems: "center" }} mt={9}>
        <Grid item>
          <Typography variant="h1" display="inline">
            {title}
          </Typography>
        </Grid>
        <Grid item xs></Grid>
        <Grid item display="flex" alignItems="center">
          <Select value={zoneUid} onChange={selectCurrentZone} sx={{ width: "320px", border: "none" }}>
            {zoneList?.map((zone, i) => {
              return (
                <MenuItem key={i} value={zone.id}>
                  {zone.zone_id}
                </MenuItem>
              );
            })}
          </Select>
          <Button
            variant="contained"
            color="warning"
            size="ui"
            startIcon={<CachedIcon />}
            loadingPosition="start"
            loading={loading}
            onClick={refresh}
            sx={{ marginLeft: "16px" }}
          >
            Refresh
          </Button>
          {UserType.ORG_USER === userType ? (
            <></>
          ) : (
            <Button
              variant="contained"
              color="success"
              size="ui"
              startIcon={<ConfirmIcon />}
              loadingPosition="start"
              loading={loading}
              onClick={apply}
              sx={{ marginLeft: "16px" }}
            >
              Apply
            </Button>
          )}
        </Grid>
      </Grid>
      <Divider my={4} />
      <Grid container>
        {tabInfo.map((info, idx) => {
          return (
            <>
              {idx > 0 ? <Grid width={"8px"} /> : <></>}
              <Grid xs>
                <Button
                  variant={url === info.url ? "outlined" : "contained"}
                  color="tab"
                  onClick={() => gotoLLMConfig(info.url)}
                  sx={{ width: "100%", height: "106px", borderWidth: "0px", borderRadius: "8px", padding: 1 }}
                >
                  <Grid container>
                    <Grid xs={12}>{info.icon}</Grid>
                    <Grid xs={12}>
                      <Typography variant="h2" pl="8px" sx={{ fontSize: "15px" }}>
                        {info.title}
                      </Typography>
                    </Grid>
                  </Grid>
                </Button>
                {url === info.url ? (
                  <Box sx={{ width: "100%", height: "38px", marginTop: "8px", background: "white", borderRadius: "8px 8px 0px 0px" }} />
                ) : (
                  <></>
                )}
              </Grid>
            </>
          );
        })}
      </Grid>
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default LLMConfigHeader;
